import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/apiService';
import { IKey, ICopy, ICheck, IEye, IEyeOff } from './DashIcons';

const BASE = process.env.REACT_APP_TAGGING_API_URL || 'https://api.railtech.io';

const MCP_CONFIG = (key) => `{
  "mcpServers": {
    "mme-memory": {
      "url": "${BASE}/mcp/http",
      "headers": {
        "Authorization": "Bearer ${key}"
      }
    }
  }
}`;

const CLAUDE_MD = `## Memory (MME)
You have MME memory tools: mme_save, mme_inject, mme_feedback.

At the start of every session:
- Call mme_inject with what we're working on

During the session, call mme_save when a decision is made,
a preference is stated, or a fact comes up worth remembering.

After each inject, call mme_feedback with accepted: true/false.`;

const clientPaths = {
  desktop: '~/Library/Application Support/Claude/claude_desktop_config.json',
  code: '~/.claude/mcp.json',
  cursor: '~/.cursor/mcp.json',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ApiKeyPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lang, setLang] = useState('python');
  const [mcpTab, setMcpTab] = useState('desktop');

  const userKey = user?.userId || user?.id || 'anon';

  const { data: keyData, isLoading, error } = useQuery({
    queryKey: ['apiKey', userKey],
    queryFn: apiService.getCurrentApiKey,
    enabled: !!user,
    retry: (failureCount, err) => (err?.response?.status === 404 ? false : failureCount < 2),
    staleTime: 30 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: ({ force } = {}) => apiService.createApiKey({ force }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['apiKey', userKey], {
        apiKey: data.apiKey,
        createdAt: new Date().toISOString(),
      });
      sessionStorage.setItem('mme_api_key', data.apiKey);
      setVisible(true);
      if (variables?.force) {
        toast.success('API key rotated');
      } else {
        toast.success(data.created ? 'API key created' : 'API key already exists');
      }
    },
    onError: (err) =>
      toast.error(err?.response?.data?.error || 'Failed to create API key'),
  });

  const apiKey = keyData?.apiKey || '';
  const hasKey = !!apiKey;
  const is404 = error?.response?.status === 404;
  // Reveal only the key prefix (`mme_live_`) — 9 chars. Previously 12
  // exposed 3 chars of entropy unnecessarily. Prefix alone is enough to
  // differentiate live vs test keys without leaking secret material.
  const masked = useMemo(
    () => (apiKey ? apiKey.slice(0, 9) + '•'.repeat(20) : ''),
    [apiKey]
  );

  const copy = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 1500);
  };

  const onRotate = () => {
    if (
      !window.confirm(
        'Rotate your API key? The current key will stop working immediately and every client will need the new one.'
      )
    ) {
      return;
    }
    createMutation.mutate({ force: true });
  };

  const keyForSnippet = visible ? apiKey : 'YOUR_API_KEY';

  const snippets = {
    python: `import requests

resp = requests.post(
    "${BASE}/memory/save",
    headers={"Authorization": "Bearer ${keyForSnippet}"},
    json={"content": "User prefers TypeScript for new services."},
)
print(resp.json())`,
    node: `import axios from "axios";

const { data } = await axios.post(
  "${BASE}/memory/save",
  { content: "User prefers TypeScript for new services." },
  { headers: { Authorization: "Bearer ${keyForSnippet}" } }
);
console.log(data);`,
    curl: `curl -X POST ${BASE}/memory/save \\
  -H "Authorization: Bearer ${keyForSnippet}" \\
  -H "Content-Type: application/json" \\
  -d '{"content": "User prefers TypeScript for new services."}'`,
  };

  return (
    <>
      <Helmet><title>API key · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <h1>API key</h1>
          <div className="sub">One key for REST, MCP, and every client. Treat it like a password.</div>
        </div>

        {isLoading && (
          <div className="panel">
            <div className="empty">
              <span className="spinner" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Loading your API key…
            </div>
          </div>
        )}

        {!isLoading && !hasKey && is404 && (
          <div className="qs-step">
            <div className="qs-num">01 · CREATE YOUR KEY</div>
            <div className="qs-title">No API key yet.</div>
            <div className="qs-desc">
              Generate a key to authenticate requests and connect MCP clients like Claude Desktop, Claude Code, or Cursor.
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => createMutation.mutate({})}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <span className="spinner" /> Creating…
                </>
              ) : (
                <>
                  <IKey size={14} /> Create API key
                </>
              )}
            </button>
          </div>
        )}

        {!isLoading && !hasKey && !is404 && error && (
          <div className="error-banner">
            {error?.response?.status === 401
              ? 'Your session may have expired. Try signing out and back in.'
              : 'Could not load your API key. The API may be temporarily unavailable.'}
          </div>
        )}

        {hasKey && (
          <>
            {/* 01 — Your key */}
            <div className="qs-step">
              <div className="qs-num">01 · YOUR KEY</div>
              <div className="qs-title">
                {visible ? 'Your secret is visible' : 'Your secret is hidden'}
              </div>
              <div className="qs-desc">
                Reveal, copy, or rotate below.
                {user?.email ? ` Issued to ${user.email}.` : ''}
                {keyData?.createdAt ? ` Created ${formatDate(keyData.createdAt)}.` : ''}
              </div>
              <div className="apikey-box">
                <code>{visible ? apiKey : masked}</code>
                <button type="button" onClick={() => setVisible((v) => !v)}>
                  {visible ? (
                    <>
                      <IEyeOff size={12} /> hide
                    </>
                  ) : (
                    <>
                      <IEye size={12} /> show
                    </>
                  )}
                </button>
                <button type="button" onClick={copy}>
                  {copied ? (
                    <>
                      <ICheck size={12} /> copied
                    </>
                  ) : (
                    <>
                      <ICopy size={12} /> copy
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onRotate}
                  disabled={createMutation.isPending}
                >
                  rotate
                </button>
              </div>
            </div>

            {/* 02 — First call */}
            <div className="qs-step">
              <div className="qs-num">02 · FIRST CALL</div>
              <div className="qs-title">Save your first memory.</div>
              <div className="qs-desc">
                Pick a language, paste into a REPL or a file, run it. You should see a{' '}
                <code>memoryId</code> come back.
              </div>
              <div style={{ marginBottom: 12 }}>
                <div className="seg">
                  {['python', 'node', 'curl'].map((l) => (
                    <button
                      type="button"
                      key={l}
                      className={lang === l ? 'on' : ''}
                      onClick={() => setLang(l)}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <pre className="code">{snippets[lang]}</pre>
            </div>

            {/* 03 — Connect a client */}
            <div className="qs-step">
              <div className="qs-num">03 · CONNECT A CLIENT</div>
              <div className="qs-title">Use the same memory from Claude, Cursor, anywhere.</div>
              <div className="qs-desc">
                One config, every MCP-capable client. Switching tools doesn't reset your context.
              </div>
              <div style={{ marginBottom: 12 }}>
                <div className="seg">
                  {[
                    { id: 'desktop', l: 'Claude Desktop' },
                    { id: 'code', l: 'Claude Code' },
                    { id: 'cursor', l: 'Cursor' },
                  ].map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      className={mcpTab === t.id ? 'on' : ''}
                      onClick={() => setMcpTab(t.id)}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="path-hint">add to {clientPaths[mcpTab]}</div>
              <pre className="code">{MCP_CONFIG(keyForSnippet)}</pre>
            </div>

            {/* 04 — Make it automatic */}
            <div className="qs-step accent">
              <div className="qs-num">04 · MAKE IT AUTOMATIC</div>
              <div className="qs-title">Let your agent save without being asked.</div>
              <div className="qs-desc">
                Drop this into your CLAUDE.md so memories happen by default — not because you remembered to say "save that."
              </div>
              <pre className="code">{CLAUDE_MD}</pre>
              <div className="path-hint" style={{ marginTop: 10 }}>
                add to ~/.claude/CLAUDE.md (mac / linux) · %USERPROFILE%\.claude\CLAUDE.md (windows)
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ApiKeyPage;
